import {ChangeDetectionStrategy, Component, computed, input, signal} from '@angular/core';
import {
  UmpireComponentOttFormat,
  UmpireComponentOttFormatList,
  UmpireComponentOttPlaylist,
  UmpireComponentOttPlaylistList,
  UmpireComponentTypeEnum,
  UmpireImageOrientation,
  UmpireOttEvent,
  UmpireOttFormat,
  UmpireOttPlaylist,
} from '@tisa-group/models';
import {CarouselModule} from '@tisa-group/client-ui/components/carousel';
import {RouterModule} from '@angular/router';
import {CommonModule} from '@angular/common';
import {RelativeTimeFormatPipe} from '@tisa-group/client-ui/pipes/relative-time-format';
import {SvgIconComponent} from '@tisa-group/client-ui/components/svg-icon';
import {ImageVariantModule} from '@tisa-group/core/pipes/image-variant';

@Component({
  selector: 'tisa-ott-list',
  templateUrl: './ott-list.component.html',
  styleUrls: ['./ott-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    RouterModule,
    CarouselModule,
    ImageVariantModule,
    RelativeTimeFormatPipe,
    SvgIconComponent,
  ],
})
export class OttListComponent {
  component = input<
    | UmpireComponentOttFormat
    | UmpireComponentOttFormatList
    | UmpireComponentOttPlaylist
    | UmpireComponentOttPlaylistList
    | undefined
  >();

  buttonRightDisabled = signal(false);

  buttonLeftDisabled = signal(true);

  items = computed(() => {
    switch (this.component()?.kind) {
      case UmpireComponentTypeEnum.OTT_FORMAT:
      case UmpireComponentTypeEnum.OTT_PLAYLIST: {
        return (
          (this.component() as UmpireComponentOttFormat | UmpireComponentOttPlaylist)
            .events || []
        );
      }
      case UmpireComponentTypeEnum.OTT_FORMAT_LIST:
        return (this.component() as UmpireComponentOttFormatList).formats || [];
      case UmpireComponentTypeEnum.OTT_PLAYLIST_LIST:
        return (this.component() as UmpireComponentOttPlaylistList).playlists || [];
      default:
        return [];
    }
  });

  mappedItems = computed(() => this.items().map(item => this.mapItem(item)));

  readonly UmpireImageOrientation = UmpireImageOrientation;

  // TODO: fetch this url from config
  readonly EXTERNAL_OTT_URL = 'https://cement.tisa-stride.tv';

  get seeMoreTarget(): string {
    let target = '';
    let slug = '';

    switch (this.component()?.kind) {
      case UmpireComponentTypeEnum.OTT_FORMAT: {
        target = 'format';
        slug = (this.component() as UmpireComponentOttFormat).format?.slug ?? '';
        break;
      }
      case UmpireComponentTypeEnum.OTT_PLAYLIST: {
        target = 'playlist';
        slug = (this.component() as UmpireComponentOttPlaylist).playlist?.slug ?? '';
        break;
      }
    }

    return target && slug ? `${this.EXTERNAL_OTT_URL}/${target}/${slug}` : '';
  }

  get isFeatured(): boolean {
    return this.component()?.is_featured || false;
  }

  get imageOrientation(): UmpireImageOrientation {
    return this.component()?.image_orientation || UmpireImageOrientation.DEFAULT;
  }

  get title(): string {
    return this.component()?.name || '';
  }

  get isVerticalImageMode() {
    return (
      this.imageOrientation === UmpireImageOrientation.VERTICAL ||
      ((this.component()?.kind === UmpireComponentTypeEnum.OTT_FORMAT_LIST ||
        this.component()?.kind === UmpireComponentTypeEnum.OTT_PLAYLIST_LIST) &&
        this.imageOrientation === UmpireImageOrientation.DEFAULT)
    );
  }

  onButtonRightStatusChanges(disabled: boolean) {
    this.buttonRightDisabled.set(disabled);
  }

  onButtonLeftStatusChanges(disabled: boolean) {
    this.buttonLeftDisabled.set(disabled);
  }

  mapItem(item: UmpireOttEvent | UmpireOttPlaylist | UmpireOttFormat) {
    // TODO: use propre lannguage when language service will be ready
    const language = 'pl';

    switch (item.type) {
      case 'format':
        return {
          title: item.name,
          image: this.isVerticalImageMode ? item.image_vertical : item.image_horizontal,
          routerLink: `${this.EXTERNAL_OTT_URL}/${language}/format/${item.slug}`,
        };
      case 'event':
        return {
          title: item.title,
          image: this.isVerticalImageMode ? item.image_vertical : item.image_horizontal,
          routerLink: `${this.EXTERNAL_OTT_URL}/${language}/watch/${item.slug}`,
          secondaryRouterLink: `${this.EXTERNAL_OTT_URL}//${language}/format/${item.format?.slug}`,
          subtitle: item.format?.name,
          startTime: item.start_time,
          status: item.external_event_status,
          kind: item.kind,
        };
      case 'playlist':
        return {
          title: item.name,
          image: this.isVerticalImageMode ? item.image_vertical : item.image_horizontal,
          routerLink: `${this.EXTERNAL_OTT_URL}/${language}/playlist/${item.slug}`,
        };
    }
  }
}
